import { Button as BPButton, FormGroup, InputGroup } from "@blueprintjs/core";
import { Colors } from "../design/colors";
import { KeyboardEventHandler, useCallback, useMemo, useRef, useState } from "react";
import { AuthErrorCodes, createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword } from "firebase/auth";
import { firebaseAuth, firebaseFunctions } from "../setup/firebase";
import { FirebaseError } from "firebase/app";
import { CollectionId, setRecord } from "../utils/firestore";
import { appHost, getEnv, Env } from "../utils/common";
import { Button } from "./Button";
import { UnsupportedBrowser } from "./UnsupportedBrowser";
import { httpsCallable } from 'firebase/functions';

enum AuthMode {
    SignIn,
    SignUp,
    PasswordReset,
    PasswordResetLinkSent,
}

const headerPrefix: () => string = () => {
    switch ( getEnv() ) {
        case Env.Prod: return "";
        case Env.Dev:
        case Env.Local: // fall through
            return "Dev ";
        case Env.Legacy:
            return "LEGACY Dev ";
        case Env.Sandbox:
            return "SANDBOX Dev ";
        default: throw new Error( `Unexpected environment setting: ${ getEnv() }` );
    }
};

export const Auth = () => {
    const [ authMode, setAuthMode ] = useState<AuthMode>( AuthMode.SignIn );
    const [ email, setEmail ] = useState<string>( "" );
    const [ organizationName, setOrganizationName ] = useState<string>( "" );
    const [ password, setPassword ] = useState<string>( "" );
    const [ showPassword, setShowPassword ] = useState<boolean>( false );
    const [ processingRequest, setProcessingRequest ] = useState<boolean>( false );
    const [ authenticationError, setAuthenticationError ] = useState<string | null>( null );
    const [ passwordInput, setPasswordInput ] = useState<HTMLInputElement | null>( null );
    const authButtonRef = useRef<HTMLDivElement>( null );
    const provisionKeypair = httpsCallable(firebaseFunctions, 'provisionKeypairOnLogin');

    const navigate = ( authMode: AuthMode ) => {
        setAuthMode( authMode );
        setAuthenticationError( null );
    };

    let header: React.ReactNode;
    let action: React.ReactNode;
    let footerElement = null;
    switch ( authMode ) {
        case AuthMode.SignIn: {
            header = (
                <span style={{ fontSize: 24, fontWeight: "bold", color: Colors.WHITE, marginBottom: 16 }}>
                  {headerPrefix()}Sign in
                </span>
            );
            const disableAction = email === "" || password === "";
            const actionFn = async () => {
                setProcessingRequest( true );
                try {
                    await signInWithEmailAndPassword( firebaseAuth, email, password );
                    // Execute keypair provisioning on Sign In
                    try {
                        await provisionKeypair();
                        console.log("Keypair provision completed");
                    } catch (error) {
                        console.error("Error provisioning keypair:", error);
                        // Don't block login if keypair provisioning fails
                    }            
                } catch ( e ) {
                    if ( e instanceof FirebaseError ) {
                        setAuthenticationError( e.code );
                    }
                }
                setProcessingRequest( false );
            };
            action = (
                <Button
                    ref={ authButtonRef }
                    style={{ marginTop: 4 }}
                    backgroundColor={ Colors.BUTTON_GREEN }
                    textColor={ Colors.WHITE }
                    disabled={ disableAction || processingRequest }
                    onClick={ actionFn }
                >
                    Sign in
                </Button>
            );
            footerElement = (
                <div style={{ marginTop: 8, color: Colors.WHITE, fontSize: 12 }}>
                    Don't have an account?&nbsp;
                    <span
                        style={{ color: Colors.LIGHT_LIGHT_GREEN, cursor: "pointer" }}
                        onClick={ () => navigate( AuthMode.SignUp ) }
                    >
                        Sign up
                    </span>
                </div>
            );
            break;
        }
        case AuthMode.SignUp: {
            header = (
                <span style={{ fontSize: 24, fontWeight: "bold", color: Colors.WHITE, marginBottom: 16 }}>
                    {headerPrefix()}Sign up
                </span>
            );
            const disableAction = email === "" || organizationName === "" || password === "";
            const actionFn = async () => {
                setProcessingRequest( true );
                try {
                    const res = await createUserWithEmailAndPassword( firebaseAuth, email, password );
                    await setRecord( CollectionId.Users, res.user.uid, { organizationName } );
                    await sendEmailVerification( res.user, { url: appHost } );
                } catch ( e ) {
                    if ( e instanceof FirebaseError ) {
                        setAuthenticationError( e.code );
                    }
                }
                setProcessingRequest( false );
            };
            action = (
                <Button
                    ref={ authButtonRef }
                    style={{ marginTop: 4 }}
                    backgroundColor={ Colors.BUTTON_GREEN }
                    textColor={ Colors.WHITE }
                    disabled={ disableAction || processingRequest }
                    onClick={ actionFn }
                >
                    Sign up
                </Button>
            );
            footerElement = (
                <div style={{ marginTop: 8, color: Colors.WHITE, fontSize: 12 }}>
                    Already have an account?&nbsp;
                    <span
                        style={{ color: Colors.LIGHT_LIGHT_GREEN, cursor: "pointer" }}
                        onClick={ () => navigate( AuthMode.SignIn ) }
                    >
                        Sign in
                    </span>
                </div>
            );
            break;
        }
        case AuthMode.PasswordReset: {
            header = (
                <span style={{ fontSize: 24, fontWeight: "bold", color: Colors.WHITE, marginBottom: 16 }}>
                    {headerPrefix()}Password reset
                </span>
            );
            const disableAction = email === "";
            const actionFn = async () => {
                setProcessingRequest( true );
                try {
                    await sendPasswordResetEmail( firebaseAuth, email, { url: appHost } );
                    navigate( AuthMode.PasswordResetLinkSent );
                    setPassword( "" );
                } catch ( e ) {
                    if ( e instanceof FirebaseError ) {
                        setAuthenticationError( e.code );
                    }
                }
                setProcessingRequest( false );
            };
            action = (
                <Button
                    ref={ authButtonRef }
                    style={{ marginTop: 4 }}
                    backgroundColor={ Colors.BUTTON_GREEN }
                    textColor={ Colors.WHITE }
                    disabled={ disableAction || processingRequest }
                    onClick={ actionFn }
                >
                    Reset password
                </Button>
            );
            footerElement = (
                <div style={{ marginTop: 8, color: Colors.WHITE, fontSize: 12 }}>
                    Back to&nbsp;
                    <span
                        style={{ color: Colors.LIGHT_LIGHT_GREEN, cursor: "pointer" }}
                        onClick={ () => navigate( AuthMode.SignIn ) }
                    >
                        sign in
                    </span>
                </div>
            );
            break;
        }
        case AuthMode.PasswordResetLinkSent: {
            header = (
                <span style={{ fontSize: 16, fontWeight: "bold", color: Colors.WHITE, marginBottom: 16 }}>
                    Check your email for the reset link
                </span>
            );
            footerElement = (
                <div style={{ marginTop: 8, color: Colors.WHITE, fontSize: 12 }}>
                    Back to&nbsp;
                    <span
                        style={{ color: Colors.LIGHT_LIGHT_GREEN, cursor: "pointer" }}
                        onClick={ () => navigate( AuthMode.SignIn ) }
                    >
                        sign in
                    </span>
                </div>
            );
            break;
        }
    }

    const handleEnterKey: KeyboardEventHandler<HTMLInputElement> = useCallback( ( e ) => {
        if ( e.key === "Enter" ) {
            e.preventDefault();
            e.currentTarget.blur();
            authButtonRef.current?.click();
        }
    }, [] );

    const onPasswordKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback( ( e ) => {
        handleEnterKey( e );
        if ( e.key === "Tab" ) {
            e.preventDefault();
            e.currentTarget.blur();
            authButtonRef.current?.focus();
        }
    }, [ handleEnterKey ] );

    const errorMessage = useMemo( () => {
        switch ( authenticationError ) {
            case null: return null;
            case AuthErrorCodes.INVALID_PASSWORD:
            case AuthErrorCodes.USER_DELETED: {
                if ( authMode === AuthMode.PasswordReset ) {
                    return "User not found";
                }
                return "Invalid user/password";
            }
            case AuthErrorCodes.EMAIL_EXISTS: return "User already exists";
            case AuthErrorCodes.INVALID_EMAIL: return "Invalid email format";
            case AuthErrorCodes.WEAK_PASSWORD: return "Weak password";
            default: return "Authentication error";
        }
    }, [ authenticationError, authMode ] );

    return (
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
            <div
                style={{
                    display: "flex",
                    backgroundColor: Colors.DARK_GREEN,
                    padding: 32,
                    boxShadow: "1px 2px 9px #222222",
                    borderRadius: 16,
                    flexDirection: "column",
                    alignItems: "center"
                }}
            >
                { header }
                <form style={{ display: 'contents' }}>
                {
                    authMode !== AuthMode.PasswordResetLinkSent ?
                    <FormGroup style={{ width: passwordInput?.clientWidth }}>
                        <InputGroup
                            style={{ backgroundColor: Colors.LIGHT_GREEN, color: Colors.WHITE }}
                            name="email"
                            placeholder="Email"
                            type="email"
                            value={ email }
                            onKeyDown={ handleEnterKey }
                            onChange={ e => setEmail( e.target.value ) }
                        />
                    </FormGroup> :
                    null
                }
                {
                    authMode === AuthMode.SignUp ?
                    <FormGroup style={{ width: passwordInput?.clientWidth }}>
                        <InputGroup
                            style={{ backgroundColor: Colors.LIGHT_GREEN, color: Colors.WHITE }}
                            name="organization"
                            placeholder="Organization"
                            value={ organizationName }
                            onKeyDown={ handleEnterKey }
                            onChange={ e => setOrganizationName( e.target.value ) }
                        />
                    </FormGroup> :
                    null
                }
                {
                    authMode === AuthMode.SignIn || authMode === AuthMode.SignUp ?
                    <FormGroup
                        helperText={
                            authMode === AuthMode.SignIn ?
                            <span
                                style={{
                                    marginTop: 16,
                                    color: Colors.LIGHT_LIGHT_GREEN,
                                    cursor: "pointer",
                                }}
                                onClick={ () => navigate( AuthMode.PasswordReset ) }
                            >
                                Forgot password?
                            </span> :
                            undefined
                        }
                    >
                        <InputGroup
                            inputRef={ setPasswordInput }
                            style={{ backgroundColor: Colors.LIGHT_GREEN, color: Colors.WHITE }}
                            type={ showPassword ? undefined : "password" }
                            name="password"
                            placeholder="Password"
                            value={ password }
                            onChange={ e => setPassword( e.target.value ) }
                            onKeyDown={ onPasswordKeyDown }
                            rightElement={
                                <BPButton
                                    minimal={ true }
                                    style={{ color: Colors.WHITE }}
                                    intent="primary"
                                    icon={ showPassword ? "eye-off" : "eye-open" }
                                    onClick={ () => setShowPassword( v => !v ) }
                                />
                            }
                        />
                    </FormGroup> :
                    null
                }
                { action }
                </form>
                <div style={{ marginTop: 4, color: Colors.YELLOW, maxWidth: passwordInput?.clientWidth }}>
                    { errorMessage }
                </div>
                { footerElement }
                { ( navigator['hid'] === undefined ) && (
                  <div style={{ marginTop: 16 }} >
                    <UnsupportedBrowser />
                  </div>
                ) }
            </div>
        </div>
    );
};
